import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, School, Mail, Sparkles, ClipboardList } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const ROLE_LABELS = {
    platform_admin: "Platform Admin",
    reviewer: "Reviewer",
    org_leader: "Organization Leader",
    coach: "Instructional Coach",
    teacher: "Teacher",
};

const ROLE_TAGLINE = {
    platform_admin: "Manage organizations, invitations, and platform-wide access.",
    reviewer:
        "Assigned organizations and audit-review workspaces will appear here starting Phase 2.",
    org_leader: "Oversee your district's schools, users, and pending invitations.",
    coach: "Coaching tools and school-level dashboards are coming in Phase 2.",
    teacher: "Reflections and evidence submission arrive in Phase 3.",
};

export default function DashboardPage() {
    const { user } = useAuth();
    if (!user) return null;

    const quickLinks = [];
    if (user.role === "platform_admin") {
        quickLinks.push(
            { to: "/app/cycles", label: "Assessment cycles", icon: ClipboardList, testId: "quick-cycles" },
            { to: "/app/admin/organizations", label: "Organizations", icon: Building2, testId: "quick-orgs" },
            { to: "/app/admin/invitations/new", label: "Invite user", icon: Mail, testId: "quick-invite" },
        );
    } else if (user.role === "org_leader" && user.org_id) {
        quickLinks.push(
            { to: "/app/cycles", label: "Assessment cycles", icon: ClipboardList, testId: "quick-cycles" },
            { to: `/app/admin/organizations/${user.org_id}`, label: "My organization", icon: School, testId: "quick-my-org" },
            { to: "/app/admin/invitations/new", label: "Invite user", icon: Mail, testId: "quick-invite" },
        );
    } else if (user.role === "reviewer") {
        quickLinks.push(
            { to: "/app/cycles", label: "Assigned cycles", icon: ClipboardList, testId: "quick-cycles" },
        );
    }

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="rounded-2xl bg-gradient-to-br from-[#0B2545] via-[#1E56A0] to-[#1E56A0] text-white p-8 relative overflow-hidden"
            >
                <div
                    aria-hidden
                    className="absolute -right-16 -top-16 w-80 h-80 rounded-full bg-[#8AC4FF]/30 blur-3xl"
                />
                <div className="relative">
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#8AC4FF] font-bold mb-3">
                        <Sparkles size={14} /> Phase 1 · Foundation
                    </div>
                    <h1 className="font-serif text-3xl sm:text-4xl font-semibold leading-tight" data-testid="dashboard-welcome">
                        Welcome, {user.full_name || user.email.split("@")[0]}.
                    </h1>
                    <p className="mt-3 max-w-2xl text-white/85">{ROLE_TAGLINE[user.role]}</p>
                </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <MetaCard
                    label="Role"
                    value={ROLE_LABELS[user.role] || user.role}
                    testId="dashboard-role"
                />
                <MetaCard
                    label="Organization"
                    value={user.organization?.name || "—"}
                    testId="dashboard-org"
                />
                <MetaCard
                    label="School"
                    value={user.school?.name || "—"}
                    testId="dashboard-school"
                />
            </div>

            {quickLinks.length > 0 && (
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                        Quick actions
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {quickLinks.map((l) => (
                            <Link
                                key={l.testId}
                                to={l.to}
                                className="rounded-xl bg-white border border-[#D6E6F2] p-5 flex items-center justify-between hover:border-[#8AC4FF] hover:shadow-lg hover:shadow-[#0B2545]/5 transition-all"
                                data-testid={l.testId}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#0B2545] text-white">
                                        <l.icon size={18} />
                                    </span>
                                    <div className="font-semibold text-[#0B2545]">{l.label}</div>
                                </div>
                                <span className="text-[#1E56A0] text-sm">Open →</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="rounded-2xl bg-white border border-[#D6E6F2] p-6">
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                    What's coming next
                </div>
                <ul className="space-y-2 text-sm text-[#0E1B2C]/85 list-disc pl-5">
                    <li>Phase 2 — Rubrics, scoring, and evidence collection.</li>
                    <li>Phase 3 — Teacher reflections and coach observations.</li>
                    <li>Phase 4 — Coherence dashboards, PDF export, benchmarks.</li>
                </ul>
            </div>
        </div>
    );
}

function MetaCard({ label, value, testId }) {
    return (
        <div
            className="rounded-2xl bg-white border border-[#D6E6F2] p-5"
            data-testid={testId}
        >
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                {label}
            </div>
            <div className="mt-2 font-serif text-2xl text-[#0B2545] font-semibold leading-tight">
                {value}
            </div>
        </div>
    );
}
